import type { ScreenRect } from "@msb/reader-bridge";
import { useEffect, useState } from "preact/hooks";
import { googleUrl, isShortLookup, lookupDefinition, wikipediaUrl, type Definition } from "../explain.ts";
import { defineMessages, useT } from "../i18n/index.ts";
import { Icon } from "../icons/Icon.tsx";
import { TranslationResult } from "./TranslationResult.tsx";

interface Props {
  term: string;
  /** Place de la sélection : sur grand écran, la définition s'ouvre à côté ; sur petit écran, en feuille du bas. */
  rect?: ScreenRect;
  sheet: boolean;
  /** Absent quand il n'y a pas de chat où aller. */
  onChat?: () => void;
  /** « Traduire » : affiche la traduction dans cette fenêtre. */
  onTranslate?: () => void;
  /** Les langues de la traduction une fois demandée (ici ou dans la barre de sélection) : elle s'affiche sous le passage. */
  translation?: { source: string; target: string };
  /** Absent quand il n'y a pas de serveur pour traduire : pas de « Traduire ». */
  translateOnServer?: (text: string, source: string, target: string, signal: AbortSignal) => Promise<string | null>;
  onClose(): void;
}

const messages = defineMessages(
  {
    passage: "Passage sélectionné",
    definitionOf: "Définition de {term}",
    quoted: "« {term} »",
    searching: "Recherche de la définition…",
    wikipedia: "Wikipédia",
    translate: "Traduire",
    explainInChat: "Expliquer dans le chat",
  },
  {
    passage: "Selected passage",
    definitionOf: "Definition of {term}",
    quoted: "“{term}”",
    searching: "Looking up the definition…",
    wikipedia: "Wikipedia",
    translate: "Translate",
    explainInChat: "Explain in the chat",
  },
);

const WIDTH = 360;

/**
 * Le sens d'une courte sélection (un mot ou deux), avec des liens pour aller plus loin. Un passage plus long
 * n'a pas de définition : la fenêtre propose seulement de l'expliquer dans le chat (comme Studio et ChatNovel).
 */
export function DefinitionSheet({ term, rect, sheet, onChat, onTranslate, translation, translateOnServer, onClose }: Props) {
  const t = useT(messages);
  const passage = !isShortLookup(term);
  const [state, setState] = useState<{ loading: boolean; definition: Definition | null }>({ loading: !passage, definition: null });
  useEffect(() => {
    if (passage) return;
    const controller = new AbortController();
    setState({ loading: true, definition: null });
    lookupDefinition(term, controller.signal)
      .then((definition) => setState({ loading: false, definition }))
      .catch(() => !controller.signal.aborted && setState({ loading: false, definition: null }));
    return () => controller.abort();
  }, [term, passage]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const style = sheet
    ? undefined
    : rect
      ? {
          left: `${Math.min(Math.max(8, rect.left + rect.width / 2 - WIDTH / 2), Math.max(8, window.innerWidth - WIDTH - 8))}px`,
          top: `${Math.min(rect.top + rect.height + 10, Math.max(8, window.innerHeight - 320))}px`,
        }
      : { left: "50%", top: "28%", transform: "translateX(-50%)" };
  return (
    <div class={sheet ? "msb-annotation-layer msb-annotation-layer--sheet" : "msb-annotation-layer"}>
      <div class="msb-annotation-scrim" onClick={onClose} />
      <div class="msb-annotation-card msb-definition" role="dialog" aria-label={passage ? t("passage") : t("definitionOf", { term })} style={style}>
        {passage ? (
          <p class="msb-definition__text msb-definition__passage">{t("quoted", { term })}</p>
        ) : (
          <>
            <h2 class="msb-definition__term">{state.definition?.title ?? term}</h2>
            {state.loading ? (
              <p role="status">{t("searching")}</p>
            ) : (
              // Rien trouvé : rien n'est dit, les liens sont là pour aller plus loin.
              state.definition && <p class="msb-definition__text">{state.definition.extract}</p>
            )}
          </>
        )}
        {translation && translateOnServer && <TranslationResult text={term} source={translation.source} target={translation.target} translateOnServer={translateOnServer} />}
        <div class="msb-annotation-card__actions msb-definition__actions">
          {!passage && (
            <a
              class="msb-logo-link msb-logo-link--wikipedia"
              href={state.definition?.url ?? wikipediaUrl(term)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("wikipedia")}
              title={t("wikipedia")}
            >
              W
            </a>
          )}
          <a class="msb-logo-link msb-logo-link--google" href={googleUrl(term)} target="_blank" rel="noopener noreferrer" aria-label="Google" title="Google">
            G
          </a>
          {onTranslate && translateOnServer && !translation && (
            <button type="button" class="msb-logo-link msb-logo-link--translate" onClick={onTranslate} aria-label={t("translate")} title={t("translate")}>
              <Icon name="translate" size={28} />
            </button>
          )}
          {/* Un mot ou deux sont seulement définis, comme dans ChatNovel : le chat est proposé pour un passage. */}
          {passage && onChat && (
            <button type="button" class="msb-pill msb-pill--primary msb-definition__chat" onClick={onChat}>
              <Icon name="chat" size={24} />
              <span class="msb-pill__label">{t("explainInChat")}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
